// ExamUnionTest.jsx
// TODO
// 🧠 Mentor reply system (chat thread per question)
// 👥 Group discussion like Doubt Forum
// 📊 AI auto-detect weak concept from mistake
// 🎯 “Repeat wrong questions automatically” engine
import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import '../learning/styles/MCQPractice.css';
import AILightComponent from '../../components/AILightComponent';

const ExamUnionTest = () => {
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Important - useEffect to stop infinite rendering
    }, [location.state.setId]);

    const [setId, setSetId] = useState(location.state.setId);
    const [setTitle, setSetTitle] = useState(location.state.setTitle);
    const [seriesId, setSeriesId] = useState(location.state.seriesId);
    const [seriesTitle, setSeriesTitle] = useState(location.state.seriesTitle);
    const [examId, setExamId] = useState(location.state.examId);
    const [examTitle, setExamTitle] = useState(location.state.examTitle);
    const [examType, setType] = useState(location.state.examType);
    const [timeLimitInMin, setTimeLimitInMin] = useState(location.state.timeLimitInMin);
    const [isRepeatPractice, setIsRepeatPractice] = useState(location.state.isRepeatPractice);
    const [isT5w, setIsT5w] = useState(location.state?.isT5w ?? 0);

    const [questionState, setQuestionState] = useState({});
    const [activeAIHelp, setActiveAIHelp] = useState(null);

    const [practiceTitle, setPracticeTitle] = useState("Think 5Whys - MCQ Practice");
    const [repeatPracticeId, setRepeatPracticeId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [timer, setTimer] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);
    const [answers, setAnswers] = useState([]);

    const stats = questions.reduce(
        (acc, _, index) => {
            const state = questionState[index] || {};

            const isSkipped = state.confidence === "skip";
            const isAnswered = state.selectedOption !== null && state.selectedOption !== undefined;

            if (isSkipped) {
            acc.skipped++;
            } 
            else if (isAnswered) {
            acc.answered++;
            } 
            else {
            acc.unanswered++;
            }

            if (state.review) {
            acc.review++;
            }

            return acc;
        },
        { answered: 0, unanswered: 0, skipped: 0, review: 0 }
    );

    const [submitted, setSubmitted] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState(Array(questions.length).fill(null));
    const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
    const [fullMarks, setFullMarks] = useState(0);
    const [totalMarksObtained, setTotalMarksObtained] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [responseData, setResponseData] = useState(null);

    useEffect(() => {
        if (!authState.isLoggedIn) return;
        fetchQuestions();
    }, [authState]);

    // 🔹 Basic counts
    const totalQuestions = questions.length;

    const attempted = answers.filter(a => a.userAnswer).length;
    const correct = answers.filter(a => a.userAnswer === a.correctAnswer).length;
    const incorrect = answers.filter(
    a => a.userAnswer && a.userAnswer !== a.correctAnswer
    ).length;

    // 🔹 Helper
    const percent = (val) =>
    totalQuestions ? ((val / totalQuestions) * 100).toFixed(1) : 0;

    // 🔹 Confidence stats function
    const getConfidenceStats = (type) => {
    const filtered = answers.filter(
        (a) => (a.confidence || "sure") === type
    );

    const total = filtered.length;
    const correct = filtered.filter(
        (a) => a.userAnswer === a.correctAnswer
    ).length;
    const incorrect = filtered.filter(
        (a) => a.userAnswer && a.userAnswer !== a.correctAnswer
    ).length;

    return {
        total,
        correct,
        incorrect,
        percent: percent(total),
    };
    };

    // 🔹 Confidence buckets
    const sureStats = getConfidenceStats("sure");
    const doubtfulStats = getConfidenceStats("doubtful");
    const guessStats = getConfidenceStats("guess");

    // 🔹 Pie chart math
    const totalConfidence =
    sureStats.total + doubtfulStats.total + guessStats.total || 1;

    const sureDeg = (sureStats.total / totalConfidence) * 360;
    const doubtfulDeg = (doubtfulStats.total / totalConfidence) * 360;

    const fetchQuestions = useCallback(() => {
        if (!setId) return;
        const config = {
            url: `/questionmaster/forsetid`,
            method: "post",
            headers: { Authorization: authState.token },
            data: {setId: setId}
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                // console.log("Question List Response:", response);
                if (response && Array.isArray(response.questionlist)) {
                    setQuestions(response.questionlist);
                } else {
                    console.error("Invalid response format: Assignment not found in response");
                }
            })
            .catch(error => {
                console.error("Error fetching Assignments:", error);
            });
    }, [fetchData, setId]);

    useEffect(() => {
        // console.log("Questions: ", questions);
    }, [questions]);

    useEffect(() => {
        if (submitted) {
            return;
        }
        setStartTime(new Date());
        const intervalId = setInterval(() => {
            setTimer(prevTimer => prevTimer + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, [submitted]);

    const handleOptionChange = (index, option) => {
        updateQuestionState(index, { selectedOption: option });

        setSelectedOptions((prev) => {
            const updated = [...prev];
            updated[index] = option;
            return updated;
        });

        setAnswers((prev) => {
            const updated = [...prev];

            if (!updated[index]) updated[index] = {};

            updated[index] = {
            ...updated[index],
            userAnswer: option,
            confidence: updated[index]?.confidence || "sure"
            };

            return updated;
        });
    };

    const handleNumericChange = (index, value) => {
        setQuestionState((prev) => ({
            ...prev,
            [index]: {
            ...prev[index],
            numericAnswer: value
            }
        }));
    };

    const handleSubjectiveChange = (index, value) => {
        setQuestionState((prev) => ({
            ...prev,
            [index]: {
            ...prev[index],
            subjectiveAnswer: value
            }
        }));
    };

    const handleSubmit = () => {
        if (submitted) return;
        const answeredQuestions = selectedOptions.filter(option => option !== null);
        if (answeredQuestions.length !== questions.length) {
            alert("Complete your test. Give all the answers before submitting it.");
            return;
        }
        const finalAnswers = questions.map((q, index) => ({
            question: q,
            userAnswer: selectedOptions[index],
            correctAnswer: q.correctAnswer,
            confidence: answers[index]?.confidence || "sure",
            correctAnswerPoints: q.correctAnswerPoints,
            wrongAnswerPoints: q.wrongAnswerPoints,
        }));
        setAnswers(finalAnswers);
        setEndTime(new Date());
        setSubmitted(true);
        showAnswers(finalAnswers);
    };

    const showAnswers = () => {
        const correctCount = questions.reduce((count, question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            if (userAnswer === correctAnswer) {
                return count + 1;
            }
            return count;
        }, 0);

        const totalQuestions = questions.length;

        let totalMarksObtained = 0;
        let fullMarks = 0;
        let totalCorrectAnswers = 0;

        const answeredQuestions = questions.map((question, index) => {
            const correctAnswer = question.mcqCorrectAnswer;
            const userAnswer = selectedOptions[index];
            const isCorrect = userAnswer === correctAnswer;
            const correctAnswerPoints = question.correctAnswerPoints;
            const wrongAnswerPoints = question.wrongAnswerPoints;
            const isCorrectAnswer = isCorrect ? 1 : 0;
            totalCorrectAnswers += isCorrectAnswer;
            const marksObtained = isCorrect ? question.correctAnswerPoints : question.wrongAnswerPoints;
            totalMarksObtained += marksObtained;
            fullMarks += question.correctAnswerPoints;
            const confidence = answers[index]?.confidence || "sure";
            return { question, correctAnswer, userAnswer, confidence, marksObtained, correctAnswerPoints, wrongAnswerPoints };
        });
        setAnswers(answeredQuestions);
        setTotalCorrectAnswers(totalCorrectAnswers);
        setFullMarks(fullMarks);
        setTotalMarksObtained(totalMarksObtained);
        const calculatedPercentage = (totalMarksObtained / fullMarks) * 100;
        setPercentage(calculatedPercentage);
    };

    const handleSavePractice = () => {
        savePracticeToDB();
    };

    const savePracticeToDB = async () => {
        const userId = authState.user._id;
        const numberOfQuestions = questions.length;
        const totalIncorrectAnswers = numberOfQuestions - totalCorrectAnswers;

        const processedAnswers = answers.map((ans) => {
            const q = ans.question; // original question object

            let questionTitle = "";

            if (q.isImageQuestion === 1) {
                questionTitle = "Question Title Has Image";
            } else {
                questionTitle = q.question || q.text || "No question text";
            }

            return {
                question: {
                    _id: q._id || q.questionId,           
                    question: questionTitle,              
                    mcqCorrectAnswer: q.mcqCorrectAnswer || ans.correctAnswer,
                    hasExplanation: q.hasExplanation || 0,
                },
                userAnswer: ans.userAnswer,
                correctAnswer: ans.correctAnswer,         
                confidence: ans.confidence || "sure",
                marksObtained: ans.marksObtained,
                correctAnswerPoints: ans.correctAnswerPoints,
                wrongAnswerPoints: ans.wrongAnswerPoints,
                userShortAnswer: ans.userShortAnswer || "",
                remarks: ans.remarks || "",
                answerTimeStarted: ans.answerTimeStarted || startTime,
                answerTimeEnded: ans.answerTimeEnded || endTime,
            };
        });
        
        const requestData = {
            isT5w: isT5w,
            setQuestionType: "MCQ",
            userId: userId,
            setId: setId,
            setTitle: setTitle,
            seriesId: seriesId,
            seriesTitle: seriesTitle,
            examId: examId,
            examTitle: examTitle,
            examType: examType,
            practiceTitle: practiceTitle,
            isRepeatPractice: isRepeatPractice,
            repeatPracticeId: repeatPracticeId,
            numberOfQuestions: numberOfQuestions,
            correctAnswers: totalCorrectAnswers,
            incorrectAnswers: totalIncorrectAnswers,
            datePracticed: startTime,
            practiceTimeStarted: startTime,
            practiceTimeEnded: endTime,
            marksObtained: totalMarksObtained,
            percentage: percentage,
            totalMarksObtained: totalMarksObtained,
            fullMarks: fullMarks,
            answers: processedAnswers,
            remarks: "",
            tags: "",
            practiceLocation: "",
            mentorName: "",
            mentorRemarks: "",
            selfFeedback: "",
            think5whysRcaId: "",
            locationIP: "",
            hasExplanation: 0,
            isActive: 1,
            isDeleted: 0,
            createBy: userId,
            timeTaken: timer,
            timeLimit: timeLimitInMin,
        };
        try {
            const config = { url: `/savepractice`, method: "post", data: requestData, headers: { Authorization: authState.token } };
            await fetchData(config);
            if(isT5w === 1) {
                navigate('/practice-mcq');
            } else {
                navigate('/examunion-practice');  
            }
        } catch (error) {
            console.error('An error occurred while fetching data:', error);
        }
    };

    const getOptionValue = (options, key) => {
        return options[key];
    };

    const isSubmitDisabled = selectedOptions.includes(null);

    const formatTime = (timeInSeconds) => {
        const hours = Math.floor(timeInSeconds / 3600);
        const minutes = Math.floor((timeInSeconds % 3600) / 60);
        const seconds = timeInSeconds % 60;

        return `${hours}Hr ${minutes}Min ${seconds}Sec`;
    };

    const handleConfidenceChange = (questionIndex, confidence) => {
        setQuestionState((prev) => {
            const current = prev[questionIndex] || {};

            if (confidence === "skip") {
                return {
                    ...prev,
                    [questionIndex]: {
                        ...current,
                        confidence: "skip",
                        selectedOption: null,
                        numericAnswer: "",
                        subjectiveAnswer: "",
                        review: false,
                    },
                };
            }

            return {
                ...prev,
                [questionIndex]: {
                    ...current,
                    confidence,
                },
            };
        });
    };

    const [animatedSureDeg, setAnimatedSureDeg] = useState(0);

    useEffect(() => {
        setTimeout(() => {
            setAnimatedSureDeg(sureDeg);
        }, 300);
    }, [sureDeg]);

    const quadrantStats = {
        mastery: 0,          // sure + correct
        overconfidence: 0,   // sure + wrong
        underconfidence: 0,  // doubtful/guess + correct
        weak: 0,             // doubtful/guess + wrong
    };

    answers.forEach((a) => {
        const isCorrect = a.userAnswer === a.correctAnswer;
        const confidence = a.confidence || "sure";

        if (confidence === "sure" && isCorrect) quadrantStats.mastery++;
        else if (confidence === "sure" && !isCorrect) quadrantStats.overconfidence++;
        else if ((confidence === "doubtful" || confidence === "guess") && isCorrect)
            quadrantStats.underconfidence++;
        else quadrantStats.weak++;
    });

    const calcAccuracy = (correct, total) => {
        if (!total) return 0;
        return ((correct / total) * 100).toFixed(1);
    };

    const sureAccuracy = calcAccuracy(sureStats.correct, sureStats.total);
    const doubtfulAccuracy = calcAccuracy(doubtfulStats.correct, doubtfulStats.total);
    const guessAccuracy = calcAccuracy(guessStats.correct, guessStats.total);

    const updateQuestionState = (index, data) => {
        setQuestionState(prev => ({
            ...prev,
            [index]: {
            ...prev[index],
            ...data
            }
        }));
    };

    const handleBookmark = (index) => {
        updateQuestionState(index, { bookmarked: true });
    };

    const intervalsRef = useRef({});

    useEffect(() => {
        return () => {
            Object.values(intervalsRef.current).forEach((intervalId) => {
            clearInterval(intervalId);
            });
        };
    }, []);

    const handleTimerToggle = (index) => {
        setQuestionState((prev) => {
            const current = prev[index] || {
            timerRunning: false,
            timeSpent: 0,
            };

            const isRunning = current.timerRunning;

            if (isRunning) {
            clearInterval(intervalsRef.current[index]);
            intervalsRef.current[index] = null;

            return {
                ...prev,
                [index]: {
                ...current,
                timerRunning: false,
                },
            };
            }

            intervalsRef.current[index] = setInterval(() => {
            setQuestionState((prevState) => {
                const q = prevState[index];
                if (!q?.timerRunning) return prevState; // safety guard

                return {
                ...prevState,
                [index]: {
                    ...q,
                    timeSpent: (q.timeSpent || 0) + 1,
                },
                };
            });
            }, 1000);

            return {
            ...prev,
            [index]: {
                ...current,
                timerRunning: true,
            },
            };
        });
    };

    const formatQuestionTimer = (seconds = 0) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;

        return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    };

    const [aiHelpPrompt, setAIHelpPrompt] = useState("");
    const [askAIHelp, setAskAIHelp] = useState(false);
    const handleGenerateAI = (question, index, selectedAnswer) => {
        if (!question) return;

        if (question.isImageQuestion === 1 || question.hasOptionImages === 1) return;

        const options = question.mcqOptions?.[0];

        if (!options) return;

        const prompt = `
            Question: ${question.question || ""}
            Options:
            A) ${options.A || ""}
            B) ${options.B || ""}
            C) ${options.C || ""}
            D) ${options.D || ""}
            Correct Answer: ${question.mcqCorrectAnswer || ""}
            Student Answer: ${selectedAnswer || ""}
            Explain why correct and where mistake happened.
        `;

        setAIHelpPrompt(prompt);
        setAskAIHelp(true);
    };
   
    const handleTagMentor = (index) => {
        const mentorCode = prompt("Enter Mentor Code:");
        const reason = prompt("Reason for tagging:");

        updateQuestionState(index, {
            mentorTagged: true,
            mentorCode,
            reason
        });
    };

    const handleTagFriends = (index) => {
        const groupCode = prompt("Enter Group Code:");
        const reason = prompt("Reason for tagging:");

        updateQuestionState(index, {
            groupTagged: true,
            groupCode,
            reason
        });
    };

    const handleSaveAIHelp = (index) => {
        const remarks = prompt("Enter remarks");

        const q = questions[index];
        const stat = questionState[index];

        const payload = {
            question: q.question,
            selectedAnswer: stat?.selectedOption,
            correctAnswer: q.mcqCorrectAnswer,
            aiPrompt: activeAIHelp?.prompt,
            remarks
        };

        updateQuestionState(index, { aiSaved: true });
    };

    const [mentorInputIndex, setMentorInputIndex] = useState(null);
    const [groupInputIndex, setGroupInputIndex] = useState(null);

    const [mentorForm, setMentorForm] = useState({ code: "", remarks: "" });
    const [groupForm, setGroupForm] = useState({ code: "", remarks: "" });

    const handleTagMentorSubmit = async (index) => {
        const q = questions[index];

        const payload = {
            userId: session.userId,
            userCode: session.userCode,
            mentorCode: mentorForm.code,
            remarks: mentorForm.remarks,
            questionId: q._id,
            questionCode: q.questionCode,
            examTitle: q.examTitle,
            seriesTitle: q.seriesTitle,
            setTitle: q.setTitle,
            taggedAt: new Date(),
            mentorResponse: ""
        };

        await postData("/examUnionTagMentor/create", payload);

        setMentorInputIndex(null);
        setMentorForm({ code: "", remarks: "" });
    };

    const handleTagGroupSubmit = async (index) => {
        const q = questions[index];

        const payload = {
            userId: session.userId,
            userCode: session.userCode,
            groupCode: groupForm.code,
            remarks: groupForm.remarks,
            questionId: q._id,
            questionCode: q.questionCode,
            examTitle: q.examTitle,
            seriesTitle: q.seriesTitle,
            setTitle: q.setTitle,
            taggedAt: new Date(),
            groupResponse: ""
        };

        await postData("/examUnionTagGroup/create", payload);

        setGroupInputIndex(null);
        setGroupForm({ code: "", remarks: "" });
    };

    const handleReviewToggle = (index) => {
        setQuestionState((prev) => {
            const current = prev[index] || {};

            return {
                ...prev,
                [index]: {
                    ...current,
                    review: !current.review,
                },
            };
        });
    };

    return (
        <div className="min-h-screen bg-black py-2 [&_p]:text-white">
            <div className="bg-gray-900 py-4 p-4 shadow-md">
                <h1 className="text-lg lg:text-xl font-bold text-yellow-300 mb-6">Test Start</h1>
                <p className="text-sm lg:text-lg text-white mb-2">Welcome, {authState.user?.name || 'Guest'}</p>
                <p className="text-sm lg:text-lg text-white mb-2">Practice Set: <span className="font-semibold">{setTitle}</span></p>
                <p className="text-sm lg:text-lg text-white mb-2">Practice Series: <span className="font-semibold">{seriesTitle}</span></p>
                <p className="text-sm lg:text-lg text-white mb-2">Practice Test for Exam: <span className="font-semibold">{examTitle} </span></p>
                <p className="text-sm lg:text-lg text-white mb-2">Start Time: <span className="font-semibold">{startTime?.toLocaleString()}</span></p>
                <p className="text-sm lg:text-lg text-yellow-400 mb-6">Time Limit: <span className="font-semibold">{timeLimitInMin} Minutes</span></p>

                <p className="text-sm lg:text-lg bg-gray-600 text-yellow-400 mb-2">Help / Instructions <span className="font-semibold"></span></p>
                <div className="flex flex-col mb-4">
                    <p>➡️ Save & Next: All answers will be saved after completion of the test.</p>
                    <p>⚪ Skip: If no option is selected, the question is marked as skipped and shown in gray.</p>
                    <p>🟡 Review: Marked for review; you can scroll up and down to revisit answers.</p>
                    <p>🟢 Answer + Review: Answered but marked for review for later checking.</p>
                    <p>🚫 Not Evaluated: Skipped questions will not be evaluated and will carry 0 marks.</p>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4 text-xs lg:text-sm text-white">
                    <div className="bg-green-600 p-2 rounded">Answered: {stats.answered}</div>
                    <div className="bg-red-600 p-2 rounded">Unanswered: {stats.unanswered}</div>
                    <div className="bg-gray-500 p-2 rounded">Skipped: {stats.skipped}</div>
                    <div className="bg-purple-600 p-2 rounded">Review: {stats.review}</div>
                </div>

                {questions.map((question, index) => (
                        <div
                            key={question._id}
                            className={`question-item p-4 mb-4 rounded-md shadow-md transition
                                ${
                                    questionState[index]?.review
                                    ? "bg-purple-500/20 border border-purple-400"
                                    : questionState[index]?.confidence === "skip"
                                    ? "bg-cyan-500/20 border border-cyan-400"
                                    : questionState[index]?.selectedOption
                                    ? "bg-yellow-500/20 border border-yellow-400"
                                    : "bg-gray-700"
                                }
                            `}
                        >
                        <p className="text-xs lg:text-sm text-white mb-2">Question# {index + 1}</p>
                        <p className="text-xs lg:text-sm text-white mb-2">Points [Correct | Incorrect]: <span className="text-green-400">{question.correctAnswerPoints}</span> / <span className="text-red-400">{question.wrongAnswerPoints}</span></p>
                        <div className="flex items-center justify-between bg-gray-800/70 px-3 py-2 rounded-xl mb-3">
                            <button 
                                type="button"
                                onClick={() => handleTimerToggle(index)}
                                className="w-20 lg:w-32 bg-indigo-500 active:scale-95 transition text-white text-xs lg:text-sm px-3 py-2 rounded-lg shadow"
                            >
                                ⏱ {questionState[index]?.timerRunning ? "Stop" : "Start"}
                            </button>
                            <div className="flex items-center space-x-1 text-yellow-300 text-xs lg:text-sm font-medium">
                                <span>⏳</span>
                                <span>{formatQuestionTimer(questionState[index]?.timeSpent)}</span>
                            </div>
                        </div>
                        <p className="text-xs lg:text-sm text-white mb-4">{question.question}</p>
                        {question.isImageQuestion === 1 && (
                            <img src={`data:image/${question.imageType};base64,${question.questionImage}`} alt={"No Image" + question.question} />
                        )}
                        <form onSubmit={(e) => e.preventDefault()}>
                            <div className="mt-3 space-y-2">
                                {question.questionType === "MCQ" && question.mcqOptions?.[0] && (
                                    <div className="grid grid-cols-1 gap-2">
                                    {["A", "B", "C", "D"].map((opt) => (
                                        <label
                                        key={opt}
                                        htmlFor={`${question._id}-${opt}`}
                                        className={`text-xs lg:text-sm flex items-center gap-2 p-2 rounded-lg cursor-pointer border transition 
                                            ${questionState[index]?.selectedOption === opt 
                                            ? "bg-indigo-600 border-indigo-400" 
                                            : "bg-gray-800 border-gray-600 hover:bg-gray-700"
                                            }`}
                                        >
                                        <input
                                            type="radio"
                                            id={`${question._id}-${opt}`}
                                            name={`question_${index}`}
                                            value={opt}
                                            checked={questionState[index]?.selectedOption === opt}
                                            disabled={!questionState[index]?.timerRunning}
                                            onChange={() => handleOptionChange(index, opt)}
                                            className="text-xs lg:text-sm accent-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed"
                                        />                                        
                                        {question.mcqOptions[0][opt] && (
                                            question.hasOptionImages === 1 ? (
                                                <img
                                                src={`data:image/${question.imageType};base64,${question.mcqOptions?.[0]?.[opt]}`}
                                                alt={`Option ${opt}`}
                                                className="max-h-32 object-contain rounded border border-gray-600"
                                                />
                                            ) : (
                                                <span className="text-xs lg:text-sm text-white">
                                                {question.mcqOptions[0][opt]}
                                                </span>
                                            )
                                        )}
                                        </label>
                                    ))}
                                    </div>
                                )}
                                {question.questionType === "Numeric" && (
                                    <div className="bg-gray-800 p-3 rounded-xl">
                                    <input
                                        type="number"
                                        placeholder="Enter your answer..."
                                        value={questionState[index]?.numericAnswer || ""}
                                        onChange={(e) =>
                                        handleNumericChange(index, e.target.value)
                                        }
                                        className="w-full px-2 py-1 rounded-lg text-black outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                    </div>
                                )}
                                {question.questionType === "SAT" && (
                                    <div className="bg-gray-800 p-3 rounded-xl">
                                    <textarea
                                        placeholder="Write your answer here..."
                                        value={questionState[index]?.subjectiveAnswer || ""}
                                        onChange={(e) =>
                                        handleSubjectiveChange(index, e.target.value)
                                        }
                                        className="w-full h-[200px] px-2 py-1 rounded-lg text-black outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                                    />
                                    </div>
                                )}
                            </div>
                            <div className="mt-2 p-2 bg-gray-800 rounded-md">
                                <p className="text-xs lg:text-sm text-gray-300 mb-2">
                                    Select Confidence:
                                </p>

                                <div className="flex gap-4">
                                    {/* Sure (Default) */}
                                    <label className="text-green-400">
                                    <input
                                        type="radio"
                                        name={`confidence_${index}`}
                                        value="sure"
                                        defaultChecked
                                        onChange={() => handleConfidenceChange(index, "sure")}
                                    />
                                    <span className="text-xs lg:text-sm ml-1">Sure</span>
                                    </label>

                                    {/* Doubtful */}
                                    <label className="text-yellow-400">
                                    <input
                                        type="radio"
                                        name={`confidence_${index}`}
                                        value="doubtful"
                                        onChange={() =>
                                        handleConfidenceChange(index, "doubtful")
                                        }
                                    />
                                    <span className="text-xs lg:text-sm ml-1">Doubtful</span>
                                    </label>

                                    {/* Guess */}
                                    <label className="text-red-400">
                                    <input
                                        type="radio"
                                        name={`confidence_${index}`}
                                        value="guess"
                                        onChange={() => handleConfidenceChange(index, "guess")}
                                    />
                                    <span className="text-xs lg:text-sm ml-1">Guess</span>
                                    </label>

                                    {/* Skip */}
                                    <label className="text-red-400">
                                    <input
                                        type="radio"
                                        name={`confidence_${index}`}
                                        value="skip"
                                        onChange={() => handleConfidenceChange(index, "skip")}
                                    />
                                    <span className="text-xs lg:text-sm ml-1">Skip</span>
                                    </label>
                                </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={questionState[index]?.review || false}
                                    // disabled={!questionState[index]?.selectedOption}
                                    disabled={!submitted && false}
                                    onChange={() => handleReviewToggle(index)}
                                />
                                <span className="text-xs text-purple-300">Mark for Review</span>
                            </div>
                        </form>
                    </div>
                ))}
                <button
                    className="submit-button w-20 lg:w-24 text-xs lg:text-sm"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                >
                    Submit
                </button>
                {submitted && (
                    <div>
                        <div className="submission-details text-white mt-4 p-4">
                            <p className="text-sm lg:text-lg !text-cyan-400 mb-4">Your Practice Test Is Over, {authState.user?.name || 'Guest'}.</p>
                            <p className="text-sm lg:text-lg">Start Time: {startTime?.toLocaleString()}</p>
                            <p className="text-sm lg:text-lg">End Time: {endTime?.toLocaleString()}</p>
                            <p className="text-sm lg:text-lg">Time Taken: {formatTime(timer)}</p>
                        </div>
                        
                        <div className="additional-stats text-yellow-300 mt-4 p-4">
                            <p className="text-sm lg:text-lg">Number of Questions: {questions.length}</p>
                            <p className="text-sm lg:text-lg">Correct Answers: {answers.filter(answer => answer.userAnswer === answer.correctAnswer).length}</p>
                            <p className="text-sm lg:text-lg">Incorrect Answers: {answers.filter(answer => answer.userAnswer !== answer.correctAnswer).length}</p>
                            <p className="text-sm lg:text-lg">Full Marks: {fullMarks}</p>
                            <p className="text-sm lg:text-lg">Marks Obtained: {totalMarksObtained}</p>
                            <p className="text-sm lg:text-lg">Percent: {percentage.toFixed(2)}%</p>
                        </div>
                    
                        <div className="mb-4 bg-gray-700 rounded-full h-12 overflow-hidden mt-4">
                            <div
                                className="progress bg-yellow-500 h-12 text-center text-white font-bold text-sm lg:text-lg py-4"
                                style={{ width: `${percentage.toFixed(2)}%` }}
                            >
                                {`${percentage.toFixed(2)}%`}
                            </div>
                        </div>
                    
                        <div className='mt-2 mb-4'><h3 className="text-cyan-300 mb-3">Question Answer Summary</h3></div>
                        
                        {answers.map((answer, index) => {
                            const isCorrect = answer.userAnswer === answer.correctAnswer;
                            const confidence = answer.confidence || "sure";

                            const getInsight = () => {
                                if (isCorrect && confidence === "sure") return { text: "Confident & Correct", color: "bg-green-500" };
                                if (isCorrect && confidence === "doubtful") return { text: "Careful but Correct", color: "bg-yellow-500" };
                                if (isCorrect && confidence === "guess") return { text: "Lucky Guess", color: "bg-orange-500" };

                                if (!isCorrect && confidence === "sure") return { text: "Overconfident Mistake", color: "bg-red-600" };
                                if (!isCorrect && confidence === "doubtful") return { text: "Uncertain & Wrong", color: "bg-yellow-600" };
                                if (!isCorrect && confidence === "guess") return { text: "Blind Guess & Wrong", color: "bg-gray-600" };

                                return { text: "Neutral", color: "bg-gray-500" };
                            };

                            const insight = getInsight();

                            return (
                                <div
                                key={index}
                                className={`answer-item p-4 mb-4 mt-4 rounded-md shadow-md transition-all duration-300 transform hover:scale-[1.01]
                                ${isCorrect ? "bg-green-900/30 border border-green-500" : "bg-red-900/30 border border-red-500"}`}
                                >
                                    <p className="text-sm lg:text-lg text-yellow-300 mb-2">
                                        Question {index + 1}: {answer.question.question}
                                    </p>
                                    <p className="text-sm lg:text-lg text-white mb-2">
                                        Your Answer:{" "}
                                        <span className={isCorrect ? "text-green-400" : "text-red-400"}>
                                        {answer.userAnswer} (
                                        {getOptionValue(answer.question.mcqOptions[0], answer.userAnswer)})
                                        </span>
                                    </p>
                                    {!isCorrect && (
                                        <p className="text-sm lg:text-lg text-red-400 mb-2">
                                        Correct Answer:{" "}
                                        <span className="text-green-400">
                                            {answer.correctAnswer} (
                                            {getOptionValue(answer.question.mcqOptions[0], answer.correctAnswer)})
                                        </span>
                                        </p>
                                    )}
                                    <p className="text-sm lg:text-lg text-white">
                                        Marks Obtained: {answer.marksObtained}
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        [Correct: {answer.correctAnswerPoints} | Wrong: {answer.wrongAnswerPoints}]
                                    </p>
                                    <div className="mt-3">
                                        <span
                                        className={`inline-block px-3 py-1 text-sm font-semibold text-white rounded-full ${insight.color}
                                        animate-pulse`}
                                        >
                                        {insight.text}
                                        </span>
                                    </div>

                                    {/* Confidence Label */}
                                    <p className="text-sm mt-2 text-gray-300">
                                        Confidence:{" "}
                                        <span
                                        className={`font-semibold ${
                                            confidence === "sure"
                                            ? "text-green-400"
                                            : confidence === "doubtful"
                                            ? "text-yellow-400"
                                            : "text-orange-400"
                                        }`}
                                        >
                                        {confidence.toUpperCase()}
                                        </span>
                                    </p>
                                    
                                    <div className="mt-2 p-3 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-lg space-y-3">
                                        {/* AI + Social */}
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-1">
                                            <button 
                                                type="button" 
                                                onClick={() =>
                                                    handleGenerateAI(question, index, questionState[index]?.selectedOption)
                                                }                                    
                                                className="text-xs lg:text-sm bg-teal-500 active:scale-95 transition text-white py-2 rounded-xl"
                                                >
                                                💾 AI Help (Prompt)
                                            </button>
                                            <button 
                                            type="button"
                                            onClick={() => handleBookmark(index)}
                                            className="bg-purple-500 active:scale-95 transition text-white py-2 rounded-xl font-medium shadow"
                                            >
                                                🔖 Bookmark
                                            </button>
                                            <button 
                                            type="button"
                                            // onClick={() => handleTagMentor(index)}
                                            onClick={() => setMentorInputIndex(index)}
                                            className="text-xs lg:text-sm bg-orange-500 active:scale-95 transition text-white py-2 rounded-xl"
                                            >
                                                👨‍🏫 Tag Mentor
                                            </button>

                                            <button 
                                            type="button"
                                            // onClick={() => handleTagFriends(index)}
                                            onClick={() => setGroupInputIndex(index)}
                                            className="text-xs lg:text-sm bg-lime-500 active:scale-95 transition text-black py-2 rounded-xl"
                                            >
                                                👥 Tag Group
                                            </button>
                                        </div>
                                    </div>
                                    {askAIHelp && (
                                        <div className="mt-3 bg-gray-900 p-3 rounded">
                                            <AILightComponent
                                            param1={`Question Help`}
                                            param2={aiHelpPrompt}
                                            />
                                            
                                            <div>
                                                <button 
                                                    type="button"
                                                    onClick={() => setAskAIHelp(false)}
                                                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 active:scale-95 transition text-white py-2 rounded-xl font-semibold shadow"
                                                >
                                                Close AI Prompt Help
                                                </button>
                                                <button 
                                                    type="button"
                                                    onClick={() => handleSaveAIHelp(index)}
                                                    disabled={true}
                                                    className="bg-cyan-500 active:scale-95 transition text-white py-2 rounded-xl"
                                                >
                                                    💾 Save AI Help
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {mentorInputIndex === index && (
                                        <div className="bg-gray-800 p-3 rounded mt-2">
                                            <input
                                            type="text"
                                            placeholder="Enter Mentor Code"
                                            className="w-full mb-2 p-2 rounded"
                                            value={mentorForm.code}
                                            onChange={(e) => setMentorForm({ ...mentorForm, code: e.target.value })}
                                            />

                                            <input
                                            type="text"
                                            placeholder="Reason"
                                            className="w-full mb-2 p-2 rounded"
                                            value={mentorForm.remarks}
                                            onChange={(e) => setMentorForm({ ...mentorForm, remarks: e.target.value })}
                                            />

                                            <button
                                            className="bg-orange-500 w-full py-2 rounded"
                                            onClick={() => handleTagMentorSubmit(index)}
                                            >
                                            Tag Now
                                            </button>
                                        </div>
                                    )}
                                    {groupInputIndex === index && (
                                        <div className="bg-gray-800 p-3 rounded mt-2">
                                            <input
                                            type="text"
                                            placeholder="Enter Group Code"
                                            className="w-full mb-2 p-2 rounded"
                                            value={groupForm.code}
                                            onChange={(e) => setGroupForm({ ...groupForm, code: e.target.value })}
                                            />

                                            <input
                                            type="text"
                                            placeholder="Reason"
                                            className="w-full mb-2 p-2 rounded"
                                            value={groupForm.remarks}
                                            onChange={(e) => setGroupForm({ ...groupForm, remarks: e.target.value })}
                                            />

                                            <button
                                            className="bg-lime-500 w-full py-2 rounded"
                                            onClick={() => handleTagGroupSubmit(index)}
                                            >
                                            Tag Now
                                            </button>
                                        </div>
                                    )}
                                    {/* ADVANCED */}
                                    <div className="mt-3 p-4 rounded-2xl bg-gradient-to-br from-indigo-900 via-gray-900 to-purple-900 shadow-xl border border-indigo-500/30 space-y-3">
                                        <div className="text-sm font-semibold text-white tracking-wide">
                                            🧠 Unlock Advanced Inputs
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs text-gray-200">
                                            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-2 text-center">
                                            🖼 Upload Mind Map
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-2 text-center">
                                            ✍️ Write Thoughts
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-2 text-center">
                                            🎤 Speak (STT)
                                            </div>
                                            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-2 py-2 text-center">
                                            ✒️ Stylus Input
                                            </div>
                                        </div>
                                        <button 
                                            type="button"
                                            onClick={() => navigate("/bookshelf")}
                                            className="w-64 lg:w-64 text-xs lg:tet-sm bg-gradient-to-r from-purple-600 to-indigo-600 active:scale-95 transition text-white py-2 rounded-xl font-semibold shadow-lg hover:shadow-purple-500/40"
                                        >
                                            🚀 Open Advanced Workspace
                                        </button>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="mt-2 bg-gray-800 p-4 rounded-md text-white">
                            <h3 className="text-lg mb-2 text-cyan-300">Confidence Breakdown</h3>

                            <div className="mb-2">
                                <p className="text-sm lg:text-lg text-green-400">Sure: {sureStats.total} ({sureStats.percent}%)</p>
                                <p className="text-sm lg:text-lg">✔ {sureStats.correct} | <span className="text-red"> ✖ {sureStats.incorrect}</span></p>
                            </div>

                            <div className="mb-2">
                                <p className="text-sm lg:text-lg text-yellow-400">Doubtful: {doubtfulStats.total} ({doubtfulStats.percent}%)</p>
                                <p className="text-sm lg:text-lg">✔ {doubtfulStats.correct} | ✖ {doubtfulStats.incorrect}</p>
                            </div>

                            <div>
                                <p className="text-sm lg:text-lg text-red-400">Guess: {guessStats.total} ({guessStats.percent}%)</p>
                                <p className="text-sm lg:text-lg">✔ {guessStats.correct} | ✖ {guessStats.incorrect}</p>
                            </div>
                        </div>

                        <div className="mt-8 flex flex-col items-center">
                            <h3 className="text-cyan-300 mb-4 text-lg font-semibold">
                                Confidence Distribution
                            </h3>

                            {/* Donut Chart */}
                            <div className="relative w-44 h-44 flex items-center justify-center">
                                <div
                                className="w-44 h-44 rounded-full transition-all duration-700 ease-out hover:scale-105"
                                style={{
                                    background: `conic-gradient(
                                    #22c55e 0deg ${sureDeg}deg,
                                    #eab308 ${sureDeg}deg ${sureDeg + doubtfulDeg}deg,
                                    #f97316 ${sureDeg + doubtfulDeg}deg 360deg
                                    )`,
                                }}
                                />

                                {/* Inner Circle (Donut effect) */}
                                <div className="absolute w-28 h-28 bg-gray-900 rounded-full flex flex-col items-center justify-center shadow-inner">
                                <p className="text-xs text-gray-400">Total</p>
                                <p className="text-lg font-bold text-white">
                                    {answers.length}
                                </p>
                                </div>
                            </div>

                            {/* Legend */}
                            <div className="mt-4 flex felx-row gap-2 space-y-2 w-full max-w-xs">
                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                                        <span className="text-green-400">Sure</span>
                                    </div>
                                    <span>{sureStats.total} ({sureStats.percent}%)</span>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                                        <span className="text-yellow-400">Doubtful</span>
                                    </div>
                                    <span>{doubtfulStats.total} ({doubtfulStats.percent}%)</span>
                                </div>

                                <div className="flex justify-between items-center text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                                        <span className="text-orange-400">Guess</span>
                                    </div>
                                    <span>{guessStats.total} ({guessStats.percent}%)</span>
                                </div>
                            </div>
                        </div>    

                        <div className="flex flex-col mt-6 w-full max-w-md">
                            <h3 className="text-cyan-300 mb-2">Confidence Accuracy</h3>

                            <div className="space-y-2 text-sm">
                                <div>
                                <p className="text-green-400">Sure Accuracy: {sureAccuracy}%</p>
                                <div className="w-full bg-gray-700 h-2 rounded">
                                    <div className="bg-green-500 h-2 rounded" style={{ width: `${sureAccuracy}%` }} />
                                </div>
                                </div>

                                <div>
                                <p className="text-yellow-400">Doubtful Accuracy: {doubtfulAccuracy}%</p>
                                <div className="w-full bg-gray-700 h-2 rounded">
                                    <div className="bg-yellow-500 h-2 rounded" style={{ width: `${doubtfulAccuracy}%` }} />
                                </div>
                                </div>

                                <div>
                                <p className="text-orange-400">Guess Accuracy: {guessAccuracy}%</p>
                                <div className="w-full bg-gray-700 h-2 rounded">
                                    <div className="bg-orange-500 h-2 rounded" style={{ width: `${guessAccuracy}%` }} />
                                </div>
                                </div>
                            </div>
                            {sureStats.incorrect > sureStats.correct && (
                                <div className="mt-4 bg-red-900/40 border border-red-500 p-3 rounded text-sm text-red-300">
                                    ⚠️ You were overconfident in several answers. Review concepts carefully.
                                </div>
                            )}     
                            <div className="mt-6 bg-gray-800 p-4 rounded text-sm text-gray-300">
                                <p className='text-sm lg:text-lg'>
                                    {sureStats.correct > sureStats.incorrect
                                    ? "✅ Your confidence is well calibrated."
                                    : "⚠️ You may be overestimating your understanding."}
                                </p>
                            </div>  
                        </div>      
                        <div className="mt-6 w-full max-w-md">
                            <div className="flex h-4 rounded overflow-hidden">
                                <div style={{ width: `${sureStats.percent}%` }} className="bg-green-500"></div>
                                <div style={{ width: `${doubtfulStats.percent}%` }} className="bg-yellow-500"></div>
                                <div style={{ width: `${guessStats.percent}%` }} className="bg-orange-500"></div>
                            </div>
                        </div>

                        <div className="mt-10">
                            <h3 className="text-cyan-300 text-lg mb-4 text-center font-semibold">
                                Confidence vs Accuracy Matrix
                            </h3>

                            <div className="grid grid-cols-2 gap-3 max-w-md mx-auto text-center text-sm">

                                {/* Mastery */}
                                <div className="bg-green-900/40 border border-green-500 p-4 rounded hover:scale-105 transition">
                                <p className="text-green-400 font-bold text-lg">{quadrantStats.mastery}</p>
                                <p className="text-green-300">🎯 Mastery</p>
                                <p className="text-xs text-gray-400">Confident & Correct</p>
                                </div>

                                {/* Overconfidence */}
                                <div className="bg-red-900/40 border border-red-500 p-4 rounded hover:scale-105 transition">
                                <p className="text-red-400 font-bold text-lg">{quadrantStats.overconfidence}</p>
                                <p className="text-red-300">⚠️ Overconfident</p>
                                <p className="text-xs text-gray-400">Confident but Wrong</p>
                                </div>

                                {/* Underconfidence */}
                                <div className="bg-yellow-900/40 border border-yellow-500 p-4 rounded hover:scale-105 transition">
                                <p className="text-yellow-400 font-bold text-lg">{quadrantStats.underconfidence}</p>
                                <p className="text-yellow-300">🤔 Underconfident</p>
                                <p className="text-xs text-gray-400">Correct but Unsure</p>
                                </div>

                                {/* Weak Area */}
                                <div className="bg-gray-800 border border-gray-500 p-4 rounded hover:scale-105 transition">
                                <p className="text-gray-300 font-bold text-lg">{quadrantStats.weak}</p>
                                <p className="text-gray-300">📉 Weak Area</p>
                                <p className="text-xs text-gray-400">Wrong & Unsure</p>
                                </div>

                            </div>
                        </div>

                        <div className="mt-6 bg-gray-800 p-4 rounded text-sm text-gray-300 max-w-md mx-auto">
                            {quadrantStats.overconfidence > quadrantStats.mastery && (
                                <p className="text-sm lg:text-lg text-red-400">
                                ⚠️ You are showing signs of overconfidence. Focus on concept clarity.
                                </p>
                            )}

                            {quadrantStats.underconfidence > 0 && (
                                <p className="text-sm lg:text-lg text-yellow-400">
                                🤔 You knew some answers but lacked confidence. Trust your preparation.
                                </p>
                            )}

                            {quadrantStats.mastery > quadrantStats.overconfidence && (
                                <p className="text-sm lg:text-lg text-green-400">
                                ✅ Strong conceptual clarity and confidence alignment.
                                </p>
                            )}

                            {quadrantStats.weak > 0 && (
                                <p className="text-sm lg:text-lg text-gray-400">
                                📉 Some topics need revision. Focus on fundamentals.
                                </p>
                            )}
                        </div>
                    
                        <button className="save-practice-button bg-green-500 text-gray-900 font-bold py-2 px-6 rounded-lg shadow-md hover:bg-green-600 mt-6" onClick={handleSavePractice}>
                            Save My Practice
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamUnionTest;
